
import { supabase } from "@/integrations/supabase/client";
import { getUserClientInfo } from "./utils";
import { validateSchoolRegistration } from "./subscription-validator";
import { checkForPastDueSubscription } from "./subscription-validator";
import { reactivateSubscription } from "./subscription-reactivator";
import { createNewSubscription } from "./subscription-creator";
import { SubscriptionParams } from "./subscription-types";

export const handlePaidSubscription = async ({ 
  user, 
  plan, 
  productId, 
  productName,
  taxRate
}: SubscriptionParams) => {
  try {
    // Check if user has an email
    if (!user.email) {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error("Could not get user information");
      if (!userData.user?.email) throw new Error("User email not found");
      
      // Use the email from the getUser call
      user.email = userData.user.email;
    }
    
    // Get user's school and client info
    const { schoolId, clientId } = await getUserClientInfo(user.email);
    
    // First, get the school_user ID for this email
    const { data: schoolUserData, error: schoolUserError } = await supabase
      .from('school_users')
      .select('id')
      .eq('email', user.email)
      .eq('school_id', schoolId)
      .maybeSingle();
    
    if (schoolUserError) {
      throw new Error(`Error finding school user: ${schoolUserError.message}`);
    }
    
    if (!schoolUserData) {
      throw new Error(`No school user found for email ${user.email} in school ${schoolId}`);
    }
    
    // Validate the school with Infradigital BEFORE creating any database records
    await validateSchoolRegistration(schoolId);
    
    // Check if the user has a past_due subscription for the same product/plan
    const pastDueSubscription = await checkForPastDueSubscription(clientId, productId);
    
    if (pastDueSubscription) {
      // Reactivate the past_due subscription by creating a new bill
      return await reactivateSubscription(
        pastDueSubscription.id, 
        productId || '', 
        plan.plan_id, 
        schoolId, 
        user.email
      );
    }
    
    // Create a new subscription if no past_due subscription exists
    return await createNewSubscription(productId, plan.plan_id, schoolId, user.email);
  } catch (error: any) {
    console.error('Error in handlePaidSubscription:', error);
    throw error;
  }
};
